/**
 * Lightweight fetcher for the caller's referral code, independent of the
 * TruCoins ledger. Used by the account page to always show the Refer & Earn
 * card for every signed-in user — including those who have never earned or
 * redeemed a coin.
 *
 * Backfills a missing `customers.referral_code` on demand so users whose
 * customer row predates the auto-generate trigger still get a code the first
 * time they open their account panel.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { isReferralEnabled } from "./referral-gate";

export type MyReferralCode = {
  referralCode: string | null;
};

export const getMyReferralCode = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyReferralCode> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: config } = await supabaseAdmin.from("loyalty_program_config")
      .select("feature_enabled, referral_enabled").eq("id", 1).maybeSingle();
    if (!isReferralEnabled(config)) return { referralCode: null };
    // loyalty_get_my_summary auto-provisions the customer row for the authed
    // user and returns the trigger-generated referral_code on new inserts.
    const supa = context.supabase as unknown as {
      rpc: (
        name: string,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
    };
    const res = await supa.rpc("loyalty_get_my_summary");
    if (res.error) throw new Error(res.error.message);
    const row = Array.isArray(res.data) && res.data.length > 0
      ? (res.data[0] as { referral_code: string | null })
      : null;
    if (row?.referral_code) return { referralCode: row.referral_code };

    // Legacy customer rows created before the auto-generate trigger may still
    // have referral_code = NULL. Backfill with the same generator.
    const codeRes = await (supabaseAdmin.rpc as unknown as (
      name: string,
    ) => Promise<{ data: string | null; error: { message: string } | null }>)(
      "gen_referral_code",
    );
    if (codeRes.error || !codeRes.data) return { referralCode: null };

    const upd = await supabaseAdmin
      .from("customers")
      .update({ referral_code: codeRes.data })
      .eq("auth_user_id", context.userId)
      .is("referral_code", null)
      .select("referral_code")
      .maybeSingle();
    return { referralCode: upd.data?.referral_code ?? codeRes.data };
  });
